import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, TrendingUp, Calendar, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import PageHeader from '@/components/PageHeader';
import { getUsers } from '@/api/api';

const CustomerSalesReport = ({ user }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedRep, setSelectedRep] = useState('all');
  const [representatives, setRepresentatives] = useState([]);

  useEffect(() => {
    // Varsayılan tarihler - bu ayın başı ve bugün
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    // MANUAL FORMATTING - Diğer sayfalarla aynı
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    setStartDate(formatDate(firstDay));
    setEndDate(formatDate(now));
    
    loadRepresentatives();
  }, []);

  // Tarih değiştiğinde otomatik veri yükle
  useEffect(() => {
    if (startDate && endDate) {
      loadData();
    }
  }, [startDate, endDate]);

  const loadRepresentatives = async () => {
    try {
      const users = await getUsers();
      // Sadece temsilci rolündeki kullanıcıları al
      const reps = users.filter(u => u.role === 'representative' || u.role === 'temsilci');
      setRepresentatives(reps);
    } catch (error) {
      console.error('Temsilciler yüklenemedi:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      // Backend YYYY-MM-DD formatı bekliyor, ISO string değil
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reports/customer-sales?token=${token}&${params.toString()}`
      );
      
      if (!response.ok) throw new Error('Veri yüklenemedi');
      
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Rapor yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Toplam istatistikler
  const totals = data.reduce((acc, item) => ({
    newCustomers: acc.newCustomers + item.new_customer_count,
    newArvento: acc.newArvento + (item.new_arvento_count || 0),
    newCamera: acc.newCamera + (item.new_camera_count || 0),
    newSensor: acc.newSensor + (item.new_sensor_count || 0),
    newM2M: acc.newM2M + (item.new_m2m_count || 0),
    existingCustomers: acc.existingCustomers + item.existing_customer_count,
    existingArvento: acc.existingArvento + (item.existing_arvento_count || 0),
    existingCamera: acc.existingCamera + (item.existing_camera_count || 0),
    existingSensor: acc.existingSensor + (item.existing_sensor_count || 0),
    existingM2M: acc.existingM2M + (item.existing_m2m_count || 0)
  }), { 
    newCustomers: 0, newArvento: 0, newCamera: 0, newSensor: 0, newM2M: 0,
    existingCustomers: 0, existingArvento: 0, existingCamera: 0, existingSensor: 0, existingM2M: 0
  });

  // Grafik verisi
  const chartData = data.map(item => ({
    name: item.representative,
    'Yeni Arvento': item.new_arvento_count || 0,
    'Mevcut Arvento': item.existing_arvento_count || 0
  }));
  
  // Kategori bazlı toplam (Peşin, Arvento Taksitli, Operatör Taksitli)
  const categoryTotals = data.reduce((acc, item) => {
    Object.entries(item.new_sales_by_category || {}).forEach(([category, count]) => {
      if (!acc[category]) acc[category] = 0;
      acc[category] += count;
    });
    Object.entries(item.existing_sales_by_category || {}).forEach(([category, count]) => {
      if (!acc[category]) acc[category] = 0;
      acc[category] += count;
    });
    return acc;
  }, {});
  
  const categoryChartData = Object.entries(categoryTotals).map(([name, value]) => ({
    name,
    value
  }));
  
  // Modern renk paleti - Gradient renkler
  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];
  
  // Custom tooltip bileşeni
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-800 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: <span className="font-bold">{entry.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  // Müşteri detayları - seçili temsilciye göre
  const filteredData = selectedRep === 'all' ? data : data.filter(d => d.representative === selectedRep);
  const allNewSales = filteredData.flatMap(d => 
    (d.new_sales_details || []).map(s => ({ ...s, representative: d.representative }))
  );
  const allExistingSales = filteredData.flatMap(d => 
    (d.existing_sales_details || []).map(s => ({ ...s, representative: d.representative }))
  );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={TrendingUp}
        title="Yeni Müşteri Satış Raporu"
        subtitle="Seçilen ay içinde portföye eklenen müşterilerin satış analizi (Ay bazlı dinamik raporlama - Tüm kategoriler: Arvento, Kamera, Sensör, M2M)"
      />

      {/* Filtreler - TEK SATIR */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-end gap-3">
          {/* Hızlı Filtre Butonları */}
          <Button
            onClick={() => {
              const today = new Date();
              const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
              const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
              const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              setStartDate(formatDate(lastMonth));
              setEndDate(formatDate(lastMonthEnd));
            }}
            variant="outline"
            size="sm"
            className="bg-gray-50 hover:bg-gray-100"
          >
            Geçen Ay
          </Button>
          <Button
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
              const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              setStartDate(formatDate(firstDay));
              setEndDate(formatDate(today));
            }}
            variant="outline"
            size="sm"
            className="bg-green-50 hover:bg-green-100"
          >
            Bu Ay
          </Button>
          <Button
            onClick={() => {
              const today = new Date();
              const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
              const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              setStartDate(formatDate(firstDayOfYear));
              setEndDate(formatDate(today));
            }}
            variant="outline"
            size="sm"
            className="bg-blue-50 hover:bg-blue-100"
          >
            Bu Yıl
          </Button>
          <Button
            onClick={() => {
              const today = new Date();
              const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              setStartDate('2020-01-01');
              setEndDate(formatDate(today));
            }}
            variant="outline"
            size="sm"
            className="bg-purple-50 hover:bg-purple-100"
          >
            Tüm Zamanlar
          </Button>
          
          {/* Tarih Seçiciler */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <label className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-600">Başlangıç</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <span className="text-gray-400">—</span>
            <div className="relative">
              <label className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-600">Bitiş</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Temsilci Filtresi */}
          <div className="relative">
            <label className="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-600">Temsilci</label>
            <select
              value={selectedRep}
              onChange={(e) => setSelectedRep(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[150px]"
            >
              <option value="all">Tüm Temsilciler</option>
              {representatives.map(rep => (
                <option key={rep.id} value={rep.username}>{rep.username}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Özet Kartlar - Mobil Uyumlu */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-4">
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg p-4 md:p-6 text-white">
          <div className="flex items-center gap-2 md:gap-3 mb-2">
            <Users className="w-6 h-6 md:w-8 md:h-8" />
            <div className="text-xs md:text-sm font-medium opacity-90">Yeni Müşteri</div>
          </div>
          <div className="text-2xl md:text-3xl font-bold">{totals.newCustomers}</div>
          <div className="text-xs md:text-sm opacity-80 mt-1">Seçilen Ay İçinde</div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-4 md:p-6 text-white">
          <div className="flex items-center gap-2 md:gap-3 mb-2">
            <Users className="w-6 h-6 md:w-8 md:h-8" />
            <div className="text-xs md:text-sm font-medium opacity-90">Toplam Portföy</div>
          </div>
          <div className="text-2xl md:text-3xl font-bold">{totals.existingCustomers}</div>
          <div className="text-xs md:text-sm opacity-80 mt-1">Tüm Müşteriler</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-4 md:p-6 text-white sm:col-span-2 lg:col-span-1">
          <div className="flex items-center gap-2 md:gap-3 mb-2">
            <Package className="w-6 h-6 md:w-8 md:h-8" />
            <div className="text-xs md:text-sm font-medium opacity-90">Toplam Satış</div>
          </div>
          <div className="text-2xl md:text-3xl font-bold">
            {totals.newArvento + totals.newCamera + totals.newSensor + totals.newM2M + 
             totals.existingArvento + totals.existingCamera + totals.existingSensor + totals.existingM2M}
          </div>
          <div className="text-xs md:text-sm opacity-80 mt-1">Tüm Kategoriler</div>
        </div>
      </div>

      {/* Yeni Müşteri Satış Kartları - Mobil Uyumlu */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4">
        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl shadow-lg p-3 md:p-5 text-white">
          <div className="text-[10px] md:text-xs font-medium opacity-90 mb-1">Yeni Müşteri</div>
          <div className="text-xl md:text-2xl font-bold">{totals.newArvento}</div>
          <div className="text-[10px] md:text-xs opacity-80 mt-1">Arvento Satış</div>
        </div>

        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl shadow-lg p-3 md:p-5 text-white">
          <div className="text-[10px] md:text-xs font-medium opacity-90 mb-1">Yeni Müşteri</div>
          <div className="text-xl md:text-2xl font-bold">{totals.newCamera}</div>
          <div className="text-[10px] md:text-xs opacity-80 mt-1">Kamera Satış</div>
        </div>

        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl shadow-lg p-3 md:p-5 text-white">
          <div className="text-[10px] md:text-xs font-medium opacity-90 mb-1">Yeni Müşteri</div>
          <div className="text-xl md:text-2xl font-bold">{totals.newSensor}</div>
          <div className="text-[10px] md:text-xs opacity-80 mt-1">Sensör Satış</div>
        </div>

        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl shadow-lg p-3 md:p-5 text-white">
          <div className="text-[10px] md:text-xs font-medium opacity-90 mb-1">Yeni Müşteri</div>
          <div className="text-xl md:text-2xl font-bold">{totals.newM2M}</div>
          <div className="text-[10px] md:text-xs opacity-80 mt-1">M2M Satış</div>
        </div>
      </div>

      {/* Mevcut Müşteri Satış Kartları - Mobil Uyumlu */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-lg p-3 md:p-5 text-white">
          <div className="text-[10px] md:text-xs font-medium opacity-90 mb-1">Mevcut Müşteri</div>
          <div className="text-xl md:text-2xl font-bold">{totals.existingArvento}</div>
          <div className="text-[10px] md:text-xs opacity-80 mt-1">Arvento Satış</div>
        </div>

        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-lg p-3 md:p-5 text-white">
          <div className="text-[10px] md:text-xs font-medium opacity-90 mb-1">Mevcut Müşteri</div>
          <div className="text-xl md:text-2xl font-bold">{totals.existingCamera}</div>
          <div className="text-[10px] md:text-xs opacity-80 mt-1">Kamera Satış</div>
        </div>

        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-lg p-3 md:p-5 text-white">
          <div className="text-[10px] md:text-xs font-medium opacity-90 mb-1">Mevcut Müşteri</div>
          <div className="text-xl md:text-2xl font-bold">{totals.existingSensor}</div>
          <div className="text-[10px] md:text-xs opacity-80 mt-1">Sensör Satış</div>
        </div>

        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-lg p-3 md:p-5 text-white">
          <div className="text-[10px] md:text-xs font-medium opacity-90 mb-1">Mevcut Müşteri</div>
          <div className="text-xl md:text-2xl font-bold">{totals.existingM2M}</div>
          <div className="text-[10px] md:text-xs opacity-80 mt-1">M2M Satış</div>
        </div>
      </div>

      {/* Grafikler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Temsilci Bazlı Grafik - Mobil Uyumlu */}
        <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-4 md:mb-6">Temsilci Bazlı Arvento Satış</h3>
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="text-gray-500">Yükleniyor...</div>
            </div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <defs>
                  <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.7}/>
                  </linearGradient>
                  <linearGradient id="colorExisting" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.7}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.5} />
                <XAxis 
                  dataKey="name" 
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis 
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="circle"
                />
                <Bar 
                  dataKey="Yeni Arvento" 
                  fill="url(#colorNew)" 
                  radius={[8, 8, 0, 0]}
                  animationDuration={1000}
                />
                <Bar 
                  dataKey="Mevcut Arvento" 
                  fill="url(#colorExisting)" 
                  radius={[8, 8, 0, 0]}
                  animationDuration={1000}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-80 flex items-center justify-center">
              <div className="text-gray-500">Veri bulunamadı</div>
            </div>
          )}
        </div>

        {/* Kategori Bazlı Grafik - Mobil Uyumlu */}
        <div className="bg-white rounded-xl shadow-md p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-4 md:mb-6">Kategori Bazlı Arvento Satış Dağılımı</h3>
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="text-gray-500">Yükleniyor...</div>
            </div>
          ) : categoryChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <defs>
                  {COLORS.map((color, index) => (
                    <linearGradient key={`gradient-${index}`} id={`pieGradient${index}`} x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.9}/>
                      <stop offset="100%" stopColor={color} stopOpacity={0.6}/>
                    </linearGradient>
                  ))}
                </defs>
                <Pie
                  data={categoryChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={{
                    stroke: '#6b7280',
                    strokeWidth: 1
                  }}
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={110}
                  innerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                  paddingAngle={3}
                  animationDuration={1000}
                >
                  {categoryChartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={`url(#pieGradient${index % COLORS.length})`}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconType="circle"
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-80 flex items-center justify-center">
              <div className="text-gray-500">Veri bulunamadı</div>
            </div>
          )}
        </div>
      </div>

      {/* Yeni Müşteri Özet Tablosu */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-green-100 bg-green-50">
          <h3 className="text-base md:text-lg font-semibold text-green-800">Yeni Müşteri Satış Raporu</h3>
          <p className="text-xs text-green-600 mt-1">Seçilen ay içinde portföye eklenen müşteriler</p>
        </div>
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="w-full min-w-[600px]">
            <thead className="bg-green-50 border-b-2 border-green-200 sticky top-0">
              <tr>
                <th className="px-3 md:px-4 py-2 md:py-3 text-left text-[10px] md:text-xs font-semibold text-green-900 sticky left-0 bg-green-50 z-10">Temsilci</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-green-900">Yeni Müşteri</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-green-900">Arvento</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-green-900">Kamera</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-green-900">Sensör</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-green-900">M2M</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-green-900">Toplam Satış</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((item, index) => {
                const newTotalSales = (item.new_arvento_count || 0) + (item.new_camera_count || 0) + 
                                      (item.new_sensor_count || 0) + (item.new_m2m_count || 0);
                
                return (
                  <tr key={index} className="hover:bg-green-50">
                    <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium text-gray-900 sticky left-0 bg-white hover:bg-green-50 z-10">{item.representative}</td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center">
                      <span className="inline-flex px-1.5 md:px-2 py-0.5 md:py-1 rounded-full bg-green-100 text-green-800 font-semibold text-[10px] md:text-xs">
                        {item.new_customer_count}
                      </span>
                    </td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center font-semibold text-green-700">
                      {item.new_arvento_count || 0}
                    </td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center font-semibold text-green-700">
                      {item.new_camera_count || 0}
                    </td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center font-semibold text-green-700">
                      {item.new_sensor_count || 0}
                    </td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center font-semibold text-green-700">
                      {item.new_m2m_count || 0}
                    </td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center font-bold text-gray-900">
                      {newTotalSales}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-green-100 border-t-2 border-green-300">
              <tr>
                <td className="px-4 py-3 text-sm font-bold text-green-900">TOPLAM</td>
                <td className="px-3 py-3 text-xs text-center">
                  <span className="inline-flex px-2 py-1 rounded-full bg-green-200 text-green-900 font-bold">
                    {data.reduce((sum, item) => sum + (item.new_customer_count || 0), 0)}
                  </span>
                </td>
                <td className="px-3 py-3 text-xs text-center font-bold text-green-900">
                  {data.reduce((sum, item) => sum + (item.new_arvento_count || 0), 0)}
                </td>
                <td className="px-3 py-3 text-xs text-center font-bold text-green-900">
                  {data.reduce((sum, item) => sum + (item.new_camera_count || 0), 0)}
                </td>
                <td className="px-3 py-3 text-xs text-center font-bold text-green-900">
                  {data.reduce((sum, item) => sum + (item.new_sensor_count || 0), 0)}
                </td>
                <td className="px-3 py-3 text-xs text-center font-bold text-green-900">
                  {data.reduce((sum, item) => sum + (item.new_m2m_count || 0), 0)}
                </td>
                <td className="px-3 py-3 text-xs text-center font-bold text-green-900 text-base">
                  {data.reduce((sum, item) => {
                    return sum + (item.new_arvento_count || 0) + (item.new_camera_count || 0) + 
                           (item.new_sensor_count || 0) + (item.new_m2m_count || 0);
                  }, 0)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Mevcut Müşteri Özet Tablosu */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-blue-100 bg-blue-50">
          <h3 className="text-base md:text-lg font-semibold text-blue-800">Mevcut Müşteri Satış Raporu</h3>
          <p className="text-xs text-blue-600 mt-1">Daha önce portföyde bulunan müşterilerin satışları</p>
        </div>
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="w-full min-w-[600px]">
            <thead className="bg-blue-50 border-b-2 border-blue-200 sticky top-0">
              <tr>
                <th className="px-3 md:px-4 py-2 md:py-3 text-left text-[10px] md:text-xs font-semibold text-blue-900 sticky left-0 bg-blue-50 z-10">Temsilci</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-blue-900">Portföy</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-blue-900">Arvento</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-blue-900">Kamera</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-blue-900">Sensör</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-blue-900">M2M</th>
                <th className="px-2 md:px-3 py-2 md:py-3 text-center text-[10px] md:text-xs font-semibold text-blue-900">Toplam Satış</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((item, index) => {
                const existingTotalSales = (item.existing_arvento_count || 0) + (item.existing_camera_count || 0) + 
                                           (item.existing_sensor_count || 0) + (item.existing_m2m_count || 0);
                
                return (
                  <tr key={index} className="hover:bg-blue-50">
                    <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm font-medium text-gray-900 sticky left-0 bg-white hover:bg-blue-50 z-10">{item.representative}</td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center">
                      <span className="inline-flex px-1.5 md:px-2 py-0.5 md:py-1 rounded-full bg-gray-100 text-gray-800 font-semibold text-[10px] md:text-xs">
                        {item.existing_customer_count}
                      </span>
                    </td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center font-semibold text-blue-700">
                      {item.existing_arvento_count || 0}
                    </td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center font-semibold text-blue-700">
                      {item.existing_camera_count || 0}
                    </td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center font-semibold text-blue-700">
                      {item.existing_sensor_count || 0}
                    </td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center font-semibold text-blue-700">
                      {item.existing_m2m_count || 0}
                    </td>
                    <td className="px-2 md:px-3 py-2 md:py-3 text-[10px] md:text-xs text-center font-bold text-gray-900">
                      {existingTotalSales}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-blue-100 border-t-2 border-blue-300">
              <tr>
                <td className="px-4 py-3 text-sm font-bold text-blue-900">TOPLAM</td>
                <td className="px-3 py-3 text-xs text-center">
                  <span className="inline-flex px-2 py-1 rounded-full bg-gray-200 text-gray-900 font-bold">
                    {data.reduce((sum, item) => sum + (item.existing_customer_count || 0), 0)}
                  </span>
                </td>
                <td className="px-3 py-3 text-xs text-center font-bold text-blue-900">
                  {data.reduce((sum, item) => sum + (item.existing_arvento_count || 0), 0)}
                </td>
                <td className="px-3 py-3 text-xs text-center font-bold text-blue-900">
                  {data.reduce((sum, item) => sum + (item.existing_camera_count || 0), 0)}
                </td>
                <td className="px-3 py-3 text-xs text-center font-bold text-blue-900">
                  {data.reduce((sum, item) => sum + (item.existing_sensor_count || 0), 0)}
                </td>
                <td className="px-3 py-3 text-xs text-center font-bold text-blue-900">
                  {data.reduce((sum, item) => sum + (item.existing_m2m_count || 0), 0)}
                </td>
                <td className="px-3 py-3 text-xs text-center font-bold text-blue-900 text-base">
                  {data.reduce((sum, item) => {
                    return sum + (item.existing_arvento_count || 0) + (item.existing_camera_count || 0) + 
                           (item.existing_sensor_count || 0) + (item.existing_m2m_count || 0);
                  }, 0)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Müşteri Detay Tablosu */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Müşteri Satış Detayları</h3>
          <select
            value={selectedRep}
            onChange={(e) => setSelectedRep(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tüm Temsilciler</option>
            {data.map(item => (
              <option key={item.representative} value={item.representative}>
                {item.representative}
              </option>
            ))}
          </select>
        </div>
        
        {/* Yeni Müşteri Satışları */}
        {allNewSales.length > 0 && (
          <>
            <div className="px-6 py-3 bg-green-50 border-b border-green-100">
              <h4 className="text-md font-semibold text-green-800">Yeni Müşteri Satışları ({allNewSales.length})</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Müşteri Adı</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Temsilci</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Satış Adedi</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tutar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {allNewSales.map((sale, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{sale.customer_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{sale.representative}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {sale.sale_date ? format(new Date(sale.sale_date), 'dd.MM.yyyy', { locale: tr }) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                          {sale.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-center font-semibold text-gray-900">{sale.quantity}</td>
                      <td className="px-6 py-4 text-sm text-right font-medium text-gray-900">
                        {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(sale.price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Mevcut Müşteri Satışları */}
        {allExistingSales.length > 0 && (
          <>
            <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 mt-4">
              <h4 className="text-md font-semibold text-blue-800">Mevcut Müşteri Satışları (5 Kasım Öncesi Kayıtlı) ({allExistingSales.length})</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Müşteri Adı</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Temsilci</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Satış Adedi</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tutar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {allExistingSales.map((sale, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{sale.customer_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{sale.representative}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {sale.sale_date ? format(new Date(sale.sale_date), 'dd.MM.yyyy', { locale: tr }) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                          {sale.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-center font-semibold text-gray-900">{sale.quantity}</td>
                      <td className="px-6 py-4 text-sm text-right font-medium text-gray-900">
                        {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(sale.price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {allNewSales.length === 0 && allExistingSales.length === 0 && (
          <div className="px-6 py-12 text-center text-gray-500">
            Seçilen kriterlere uygun satış bulunamadı
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerSalesReport;
